import React from "react";
import { Button } from "./ui/button";
import { X } from "lucide-react";
import { ScrollArea } from "./ui/scroll-area";
import { Inventory } from "../lib/api";
import { Icons } from "./ui/icons";
import SidePanel, { PanelHeader } from "./side-panel";
import { useIsMobile } from "../hooks/use-is-mobile";

interface ItemDetailsDrawerProps {
  selectedItemDetails: Inventory | null;
  onClose: () => void;
}

interface DetailField {
  label: string;
  value: React.ReactNode;
  strong?: boolean;
}

const MobileDetailCard: React.FC<{ title: string; fields: DetailField[] }> = ({
  title,
  fields,
}) => (
  <section className="rounded-2xl border border-gray-100 bg-white">
    <h3 className="px-4 pt-3 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
      {title}
    </h3>
    <dl className="divide-y divide-gray-100">
      {fields.map((field) => (
        <div
          key={field.label}
          className="flex items-start justify-between gap-4 px-4 py-3"
        >
          <dt className="shrink-0 text-sm text-gray-500">{field.label}</dt>
          <dd
            className={`min-w-0 break-words text-right text-sm text-gray-900 ${
              field.strong ? "font-semibold" : "font-medium"
            }`}
          >
            {field.value}
          </dd>
        </div>
      ))}
    </dl>
  </section>
);

const ItemDetailsDrawer: React.FC<ItemDetailsDrawerProps> = ({
  selectedItemDetails,
  onClose,
}) => {
  const isMobile = useIsMobile();

  return (
    <SidePanel open={!!selectedItemDetails} onClose={onClose} width="w-[700px]">
      {selectedItemDetails && (
        <ItemDetailsContent
          item={selectedItemDetails}
          isMobile={isMobile}
          onClose={onClose}
        />
      )}
    </SidePanel>
  );
};

const ItemDetailsContent: React.FC<{
  item: Inventory;
  isMobile: boolean;
  onClose: () => void;
}> = ({ item, isMobile, onClose }) => {
  const itemFields: DetailField[] = [
    { label: "Item Code", value: item.itemCode, strong: true },
    { label: "Description", value: item.description },
    { label: "Unit Weight", value: `${item.unitWeight} kg` },
    {
      label: "Length",
      value:
        item.length != null &&
        String(item.length).trim() !== "" &&
        String(item.length) !== "undefined"
          ? String(item.length)
          : "—",
    },
    { label: "Unit Value", value: Number(item.value || 0).toLocaleString() },
    {
      label: "Hire Rate / Week",
      value: Number(item.hireRatePerWeek || 0).toLocaleString(),
    },
    {
      label: "Total Value",
      value: (
        Number(item.value || 0) * Number(item.totalQuantity || 0)
      ).toLocaleString(),
      strong: true,
    },
    { label: "Warehouse", value: item.warehouse?.name || "N/A" },
    { label: "Total Quantity", value: item.totalQuantity, strong: true },
  ];

  const warehouseStatusBadge = item.warehouse && (
    <span
      className={`px-2 py-1 rounded-full text-xs ${
        item.warehouse.status === "ACTIVE"
          ? "bg-green-100 text-green-800"
          : "bg-red-100 text-red-800"
      }`}
    >
      {item.warehouse.status}
    </span>
  );

  const warehouseFields: DetailField[] = item.warehouse
    ? [
        { label: "Name", value: item.warehouse.name },
        { label: "Status", value: warehouseStatusBadge },
        {
          label: "Location",
          value: `${item.warehouse.street}, ${item.warehouse.ward}, ${item.warehouse.district}`,
        },
        {
          label: "Contact",
          value: (
            <>
              {item.warehouse.pointOfContactName}
              <br />
              {item.warehouse.pointOfContactPhone}
            </>
          ),
        },
      ]
    : [];

  const timestampFields: DetailField[] = [
    { label: "Created At", value: new Date(item.createdAt).toLocaleString() },
    { label: "Updated At", value: new Date(item.updatedAt).toLocaleString() },
  ];

  const quantityTiles = (
    <>
      <div className="flex items-center gap-3 p-3 bg-green-50 rounded-lg">
        <Icons.InStock className="w-5 h-5 text-green-600" />
        <div>
          <p className="text-sm font-medium text-gray-600">Available</p>
          <p className="text-lg font-semibold text-green-600">
            {item.availableQuantity}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
        <Icons.RentedOut className="w-5 h-5 text-blue-600" />
        <div>
          <p className="text-sm font-medium text-gray-600">Rented Out</p>
          <p className="text-lg font-semibold text-blue-600">
            {item.rentedQuantity}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 p-3 bg-orange-50 rounded-lg">
        <Icons.Reserved className="w-5 h-5 text-orange-600" />
        <div>
          <p className="text-sm font-medium text-gray-600">Reserved</p>
          <p className="text-lg font-semibold text-orange-600">
            {item.reservedQuantity}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 p-3 bg-red-50 rounded-lg">
        <Icons.Damaged className="w-5 h-5 text-red-600" />
        <div>
          <p className="text-sm font-medium text-gray-600">Damaged</p>
          <p className="text-lg font-semibold text-red-600">
            {item.damagedQuantity}
          </p>
        </div>
      </div>
      <div className="flex items-center gap-3 p-3 bg-gray-100 rounded-lg">
        <Icons.Lost className="w-5 h-5 text-gray-700" />
        <div>
          <p className="text-sm font-medium text-gray-600">Lost</p>
          <p className="text-lg font-semibold text-gray-800">
            {item.lostQuantity || 0}
          </p>
        </div>
      </div>
    </>
  );

  const renderDesktopGrid = (fields: DetailField[]) => (
    <div className="grid grid-cols-2 gap-4">
      {fields.map((field) => (
        <div key={field.label}>
          <label className="text-sm font-medium text-gray-600">
            {field.label}
          </label>
          <p className={field.strong ? "text-sm font-semibold" : "text-sm"}>
            {field.value}
          </p>
        </div>
      ))}
    </div>
  );

  return (
    <div className="relative h-full flex flex-col">
      {isMobile ? (
        <PanelHeader
          title={item.itemCode}
          subtitle={item.description}
          onClose={onClose}
        />
      ) : (
        <div className="px-6 py-4 flex-shrink-0 border-b border-gray-100">
          <div className="flex items-start gap-3">
            <h2 className="text-[22px] font-semibold text-foreground font-jakarta mr-auto">
              {item.itemCode}
            </h2>
            <Button variant="outline" className="h-8 w-8 p-0" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          <p className="text-gray-600 text-sm mt-1">{item.description}</p>
        </div>
      )}
      <div className="flex-grow overflow-hidden">
        <ScrollArea className="h-full">
          {isMobile ? (
            <div className="w-full space-y-4 p-4 pb-8">
              <MobileDetailCard title="Item Details" fields={itemFields} />
              <section className="rounded-2xl border border-gray-100 bg-white p-4">
                <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                  Quantity Breakdown
                </h3>
                <div className="grid grid-cols-2 gap-3">{quantityTiles}</div>
              </section>
              {item.warehouse && (
                <MobileDetailCard
                  title="Warehouse Information"
                  fields={warehouseFields}
                />
              )}
              <MobileDetailCard title="Timestamps" fields={timestampFields} />
            </div>
          ) : (
            <div className="w-full p-6 space-y-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-4">Item Details</h3>
                {renderDesktopGrid(itemFields)}
              </div>

              <div className="bg-white border rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-4">
                  Quantity Breakdown
                </h3>
                <div className="grid grid-cols-2 gap-4">{quantityTiles}</div>
              </div>

              {item.warehouse && (
                <div className="bg-white border rounded-lg p-4">
                  <h3 className="font-semibold text-lg mb-4">
                    Warehouse Information
                  </h3>
                  {renderDesktopGrid(warehouseFields)}
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-semibold text-lg mb-4">Timestamps</h3>
                {renderDesktopGrid(timestampFields)}
              </div>
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
};

export default ItemDetailsDrawer;
